import type {
  LatestPointsEntry,
  PlayerAchievementBadge,
  PlayerProfile,
  RecentGameEntry,
  SeasonResultEntry,
} from '@ottd/api';
import { usePlayer } from '@ottd/api';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AchievementBadge } from '@/components/prototype/achievement-badge';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { RelativeTime } from '@/components/prototype/relative-time';
import { ProgressBar } from '@/components/prototype/progress-bar';
import { QueryState } from '@/components/prototype/query-state';
import { StatTile } from '@/components/prototype/stat-tile';
import { Badge } from '@/components/ui/badge';
import { clampPercent, formatNumber, formatSignedPoints } from '@/lib/format';
import { cn } from '@/lib/utils';
import { PersonalBests } from '@/pages/player/personal-bests';
import { formatMonthYear } from '@/pages/shared';

export default function PlayerProfilePage() {
  const { t } = useTranslation('player');
  const { name } = useParams<{ name: string }>();
  const { data: profile, isLoading, isError, error } = usePlayer(name);

  return (
    <div className="mx-auto max-w-[1080px] px-4 py-6 sm:px-5">
      <QueryState
        className="flex flex-col gap-6"
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={!profile}
        emptyLabel={t('profile.notFound')}
      >
        {profile ? (
          <>
            <ProfileHeader profile={profile} />
            <BevelWindow
              title={t('profile.achievementsTitle')}
              action={t('profile.achievementsEarned', { count: profile.stats.achievements })}
            >
              <AchievementGrid earned={profile.achievements} />
              <AccountAchievements earned={profile.accountAchievements} />
            </BevelWindow>
            <div className="grid gap-6 lg:grid-cols-[1.25fr_1fr]">
              <LatestPoints entries={profile.latestPoints} />
              <SeasonResults entries={profile.seasonResults} />
            </div>
            <PersonalBests entries={profile.personalBests} />
            <RecentGames entries={profile.recentGames} />
          </>
        ) : null}
      </QueryState>
    </div>
  );
}

function ProfileHeader({ profile }: { profile: PlayerProfile }) {
  const { t } = useTranslation('player');
  const xpPct =
    profile.xp.next > 0 ? clampPercent((profile.xp.current / profile.xp.next) * 100) : 0;
  return (
    <BevelWindow
      shadow="lg"
      title={t('profile.title')}
      action={
        profile.seasonRank !== null
          ? t('profile.seasonRank', { rank: profile.seasonRank })
          : undefined
      }
    >
      <div className="flex flex-wrap items-start gap-4">
        <PlayerAvatar name={profile.name} src={profile.avatar} size={72} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-strong text-xl">{profile.name}</h1>
            {profile.isRegistered ? (
              <Badge variant="success">{t('profile.registered')}</Badge>
            ) : null}
            {profile.isBanned ? <Badge variant="orange">{t('profile.banned')}</Badge> : null}
          </div>
          <p className="text-dim mt-1 font-mono text-xs">
            {t('profile.memberSince', { date: formatMonthYear(profile.memberSince) })}
          </p>
        </div>
        <div className="ml-auto grid grid-cols-2 gap-2 sm:grid-cols-5">
          <StatTile
            value={formatNumber(profile.careerPoints)}
            label={t('profile.careerScore')}
            tone="orange"
          />
          <StatTile
            value={formatNumber(profile.stats.totalGames)}
            label={t('profile.totalGames')}
          />
          <StatTile value={formatNumber(profile.stats.gamesWon)} label={t('profile.gamesWon')} />
          <StatTile
            value={formatNumber(profile.stats.achievements)}
            label={t('profile.achievements')}
            tone="gold"
          />
          <StatTile
            value={profile.stats.playTimeHuman}
            label={t('profile.playTime')}
            tone="green"
          />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="font-display text-orange text-xs">
          {t('profile.level', { level: profile.level })}
        </span>
        <ProgressBar
          className="min-w-[160px] flex-1"
          value={xpPct}
          height={12}
          label={t('profile.levelProgress', { level: profile.level })}
        />
        <span className="text-dim font-mono text-[11px]">
          {t('profile.xpToNext', {
            current: profile.xp.current,
            next: profile.xp.next,
            level: profile.level + 1,
          })}
        </span>
      </div>
    </BevelWindow>
  );
}

function AchievementGrid({ earned }: { earned: PlayerAchievementBadge[] }) {
  const { t } = useTranslation('player');

  // Data-driven: render what the API actually awards (each badge carries its own
  // label + level). The prototype's fixed 8-key "locked" grid used fictional
  // namespaces that don't exist in the backend, so it's not rendered (review
  // HIGH / audit G10) - the count in the window title conveys totals.
  if (earned.length === 0) {
    return <p className="text-faint font-mono text-xs">{t('profile.achievementsEmpty')}</p>;
  }
  return <BadgeGrid badges={earned} />;
}

/**
 * The linked account's legacy user-level badges - the retired `/user/{id}`
 * page showed them. Historical only (nothing awards them now), so the block
 * simply doesn't render for an account without any.
 */
function AccountAchievements({ earned }: { earned: PlayerAchievementBadge[] }) {
  const { t } = useTranslation('player');

  if (earned.length === 0) return null;
  return (
    <section className="border-line mt-4 border-t pt-3">
      <h3 className="font-display text-dim mb-2 text-[10px] uppercase">
        {t('profile.accountAchievements')}
      </h3>
      <BadgeGrid badges={earned} />
    </section>
  );
}

function BadgeGrid({ badges }: { badges: PlayerAchievementBadge[] }) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
      {badges.map((badge) => (
        <AchievementBadge
          key={`${badge.key}-${badge.level ?? 0}`}
          keyName={badge.key}
          label={badge.label}
          description={badge.description}
          level={badge.level}
          achievedAt={badge.achievedAt}
        />
      ))}
    </div>
  );
}

/**
 * The API's `context` already ends with the game id ("City Builder #608566")
 * and the id is rendered beside it as the link, so strip the trailing copy
 * rather than printing it twice. Tolerates a context that omits it.
 */
function contextLabel({ context, gameId }: LatestPointsEntry): string {
  const label = (context ?? '').trim();
  const suffix = `#${gameId}`;

  return label.endsWith(suffix) ? label.slice(0, -suffix.length).trim() : label;
}

function LatestPoints({ entries }: { entries: LatestPointsEntry[] }) {
  const { t } = useTranslation('player');

  return (
    <BevelWindow title={t('profile.latestScore')}>
      {entries.length === 0 ? (
        <p className="text-faint font-mono text-xs">{t('profile.latestScoreEmpty')}</p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((entry, i) => {
            const label = contextLabel(entry);

            return (
              <li
                key={`${entry.gameId}-${i}`}
                className="border-line flex items-baseline gap-2 border-b py-1.5 last:border-0"
              >
                <span className="text-green font-display w-12 text-xs">
                  {formatSignedPoints(entry.points)}
                </span>
                {entry.isWinner ? (
                  <span className="text-gold font-display shrink-0 text-[10px] uppercase">
                    {t('profile.asWinner')}
                  </span>
                ) : null}
                <span className="text-text min-w-0 flex-1 truncate font-mono text-xs">
                  {label ? `${label} ` : ''}
                  <Link to={`/game/${entry.gameId}`} className="text-orange">
                    #{entry.gameId}
                  </Link>
                  {entry.goal ? (
                    <span className="text-dim"> · {t('profile.goal', { goal: entry.goal })}</span>
                  ) : null}
                </span>
                <RelativeTime
                  iso={entry.at}
                  label={entry.when}
                  className="text-faint font-mono text-[11px]"
                />
              </li>
            );
          })}
        </ul>
      )}
    </BevelWindow>
  );
}

function SeasonResults({ entries }: { entries: SeasonResultEntry[] }) {
  const { t } = useTranslation('player');
  const { t: tc } = useTranslation('common');

  return (
    <BevelWindow title={t('profile.seasonResults')}>
      {entries.length === 0 ? (
        <p className="text-faint font-mono text-xs">{t('profile.seasonResultsEmpty')}</p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((entry) => (
            <li
              key={entry.num}
              className="border-line flex items-baseline gap-2 border-b py-1.5 last:border-0"
            >
              <span
                className={cn(
                  'font-display w-8 text-xs',
                  entry.rank <= 3 ? 'text-gold' : 'text-dim',
                )}
              >
                #{entry.rank}
              </span>
              <Link to={`/season/${entry.num}`} className="text-green flex-1 font-mono text-xs">
                {t('profile.season', { num: entry.num })}
              </Link>
              <span className="text-dim font-mono text-[11px]">
                {tc('units.games', { count: entry.games })}
              </span>
              <span className="text-green font-mono text-xs">
                {formatSignedPoints(entry.points)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </BevelWindow>
  );
}

/*
 * `place` is a stable machine code (`WON` / `LIVE` / `VOID`, or an English
 * ordinal for every other finishing position) - the API keeps it untranslated
 * precisely because it keys the styling below. The visible label comes from
 * `rank` when the entry has one, and from the code otherwise.
 */
const PLACE_TONE: Record<string, string> = {
  WON: 'text-gold',
  LIVE: 'text-green',
  // A game abandoned mid-run: no result, no points to show.
  VOID: 'text-faint',
};

/** Places that never carry a score: a running game, and an abandoned one. */
const PLACE_POINTS: Record<string, string> = {
  LIVE: '· · ·',
  VOID: '-',
};

/** The visible finishing position: a localised ordinal, else the coded state. */
function placeLabel(entry: RecentGameEntry, t: TFunction<'player'>): string {
  if (entry.rank !== null && entry.rank !== undefined) {
    return t('profile.placeRank', { count: entry.rank, ordinal: true });
  }
  return t(`profile.place.${entry.place.toLowerCase()}`, { defaultValue: entry.place });
}

function RecentGames({ entries }: { entries: RecentGameEntry[] }) {
  const { t } = useTranslation('player');

  return (
    <BevelWindow title={t('profile.recentGames')}>
      {entries.length === 0 ? (
        <p className="text-faint font-mono text-xs">{t('profile.recentGamesEmpty')}</p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className={cn(
                'flex flex-wrap items-baseline gap-2 px-2 py-1.5',
                entry.place === 'WON' && 'bg-panel-in',
              )}
            >
              <span
                className={cn(
                  // Wide enough for the longest translated state ("WYGRANA"),
                  // and fixed so the ids beside it still line up as a column.
                  'font-display w-24 shrink-0 truncate text-xs',
                  PLACE_TONE[entry.place] ?? 'text-dim',
                )}
              >
                {placeLabel(entry, t)}
              </span>
              <Link to={`/game/${entry.id}`} className="text-orange w-16 font-mono text-xs">
                #{entry.id}
              </Link>
              <span className="text-text text-sm">{entry.server}</span>
              <span className="text-dim min-w-0 flex-1 truncate font-mono text-xs">
                {entry.outcome}
              </span>
              <RelativeTime
                iso={entry.at}
                label={entry.when}
                className="text-faint font-mono text-[11px]"
              />
              <span
                className={cn(
                  'font-display w-16 text-right text-[11px]',
                  PLACE_TONE[entry.place] ?? 'text-green',
                )}
              >
                {PLACE_POINTS[entry.place] ?? formatSignedPoints(entry.points)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </BevelWindow>
  );
}
